import React, { useEffect, Fragment, } from 'react';
import { Button, Modal, ModalBody, ModalFooter, Form, } from 'reactstrap';
import { isError, chunk, isEmpty, pickBy, pick, get, keyBy, omit, mapValues, } from 'lodash';
import { useToggle, useList, } from 'react-use';
import { addDays, } from 'date-fns';
import { toast, } from 'react-toastify';

import firebase, { functions } from '../../firebase';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useFormState from '../hooks/useFormState';
import Field from '../Field';
import { getAddressByPostalCode, } from '../../util';
import { batch, } from '../../shared/firebase';
import { deliveryTimes, } from '../../shared/config';
import { fields, itemFields, combinedItemFields, getCombinedItems, } from '../../shared/models/combiningTemplate';
import ListForm from '../ListForm';
import ProductSelectOptionLabel from '../ProductSelectOptionLabel';
import OverlayLoading from '../OverlayLoading';

const db = firebase.firestore();
const fetchFulfillmentInboundShipmentPlan = functions.httpsCallable('fetchFulfillmentInboundShipmentPlan');
const { entries, } = Object;

export default function CombiningTemplateFormModal(props) {
  const { isOpen, values, senders, tenant, products, newItems, onClickClose, } = props;
  const isNew = !values || !values.id;
  const productOptions = products.map((product) => {
    const { id, ref, sku, code, name, asin, } = product;
    return {
      value: id,
      queryLabel: [id, sku, code, asin].join(' '),
      label: (
        <ProductSelectOptionLabel product={product} />
      ),
    };
  });
  const productsById = keyBy(products, 'id');
  const productsBySku = keyBy(products, 'sku');
  const statedFields = useFormState(values, fields({ products }), isOpen);
  const [items, { set: setItems, updateAt: updateItemAt }] = useList(values.items || newItems || [{ productId: null, quantity: 1, }]);
  const [combinedItems, { set: setCombinedItems, updateAt: updateCombinedItemAt }] = useList(getCombinedItems(values).length > 0 ? getCombinedItems(values) : [{ productId: null, quantity: 1, }]);
  const [isSubmitting, toggleSubmitting] = useToggle(false);
  const isUnsubmittable =
    Object.values(statedFields).some(_ => !_.isValid) ||
    items.length === 0 ||
    items.some(_ => !_.isValid) ||
    combinedItems.length === 0 ||
    combinedItems.some(_ => !_.isValid);
  const onSubmit = async (event) => {
    event.preventDefault();
    if(isUnsubmittable) return;

    toggleSubmitting(true);
    try {
      const _items = items.map(_ => ({ ..._, sku: get(productsById, [_.productId, 'sku'], ''), quantity: _.quantity, }));
      const _combinedItems = combinedItems.map(_ => ({ ..._, sku: get(productsById, [_.productId, 'sku'], ''), quantity: _.quantity, }));
      let plans = null;
      await props.onSubmit({ ...mapValues(statedFields, 'value'), items: _items.map(_ => omit(_, 'isValid')), combinedItems: _combinedItems.map(_ => omit(_, 'isValid')), });
    } catch(e) {
      toast.error(`エラーが発生しました (${e.message})`, { autoClose: false });
      throw e;
    } finally {
      toggleSubmitting(false);
    }
  };

  return (
    <Modal className="position-relative" isOpen={isOpen} style={{ minWidth: 800 }}>
      <div className="modal-header">
        <div className="w-100 d-flex justify-content-between">
          <h5 className="modal-title">
            加工組{isNew ? '追加' : '編集'}
          </h5>
        </div>
      </div>
      <Form onSubmit={onSubmit}>
        <ModalBody className="overflow-auto" style={{ minHeight: 600, maxHeight: '70vh' }}>
          <div>
            {
              entries(statedFields).map(([fieldName, fieldSetting]) => {
                return (
                  <Field
                    key={fieldName}
                    name={fieldName}
                    {...fieldSetting}
                  />
                );
              })
            }
          </div>
          <hr className="my-5" />
          <div>
            <h5>加工元商品</h5>
            <ListForm
              initialItem={_ => ({ quantity: 1, })}
              addButtonLabel="加工元商品追加"
              items={items}
              renderItem={(item, itemIndex) => {
                return (
                  <div className="card p-3">
                    <ItemForm
                      fields={itemFields({ items, products, })}
                      index={itemIndex}
                      values={item}
                      product={productsById[item.productId]}
                      products={products}
                      productOptions={productOptions}
                      tenant={tenant}
                      onChange={_ => updateItemAt(itemIndex, { ...item, ..._, })}
                    />
                  </div>
                );
              }}
              onChange={setItems}
              minItems={1}
            />
          </div>
          <hr className="my-5" />
          <div>
            <h5>完成商品</h5>
            <ListForm
              initialItem={_ => ({ quantity: 1, })}
              addButtonLabel="完成商品追加"
              items={combinedItems}
              renderItem={(item, itemIndex) => {
                return (
                  <div className="card p-3">
                    <ItemForm
                      fields={combinedItemFields({ items: combinedItems, products, })}
                      index={itemIndex}
                      values={item}
                      product={productsById[item.productId]}
                      products={products}
                      productOptions={productOptions}
                      tenant={tenant}
                      onChange={_ => updateCombinedItemAt(itemIndex, { ...item, ..._, })}
                    />
                  </div>
                );
              }}
              onChange={setCombinedItems}
              minItems={1}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="cancel" color="secondary" onClick={onClickClose}>閉じる</Button>
          <Button className="save" type="submit" color="primary" onClick={onSubmit} disabled={isUnsubmittable || isSubmitting}>保存</Button>
        </ModalFooter>
      </Form>
      <OverlayLoading isOpen={isSubmitting} label="処理中..." />
    </Modal>
  );
};

function ItemForm (props) {
  const { index: itemIndex, fields, values, tenant, product, onChange, disabled = false, products, productOptions, } = props;
  const statedFields = useFormState(values, fields, values);
  useEffect(() => {
    onChange({
      ...mapValues(statedFields, 'value'),
      isValid: Object.values(statedFields).every(_ => _.isValid),
    });
  }, [...Object.values(statedFields).map(_ => _.value), ...Object.values(statedFields).map(_ => _.isValid)]);

  return (
    <div>
      <div className="d-flex">
        {
          entries(statedFields).map(([fieldName, fieldSetting], i) => (
            <div className="mr-2" style={{ minWidth: fieldName === 'productId' && 500 }}>
              <Field
                key={fieldName}
                name={fieldName}
                {...fieldSetting}
                readOnly={_ => disabled}
                {...(itemIndex === 0 && i === 0 && { inputProps: { autoFocus: true, } })}
                {...(fieldName === 'productId' && {
                  options: productOptions,
                  inputProps: {
                    styles: { 
                      valueContainer: _ => ({ ..._, height: 60 }),
                      singleValue: _ => ({ ..._, top: 0, transform: '' }),
                    },
                  },
                })}
              />
            </div>
          ))
        }
      </div>
    </div>
  );
}
